import { ChangeDetectionStrategy, Component, computed, inject, input } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { firstValueFrom } from 'rxjs';
import { BsDropdownModule } from 'ngx-bootstrap/dropdown';
import { TranslateModule } from '@ngx-translate/core';

import { ESelectedEntityType, type SelectedEntity } from '@common/types/layout.type';

import type { NormalizedGridEntity, NormalizedTabEntity } from '@web/types';

import { LayoutFacade } from '@web/store/layout/layout.facade';
import { TabsFacade } from '@web/store/page-structure/tabs/tabs.facade';
import { GridsFacade } from '@web/store/page-structure/grids/grids.facade';

import { WebTabFormService } from '@web/core/services/web-tab-form.service';

import { NavigationGridItemComponent } from '../navigation-grid-item';

import { NavigationDrawerService } from '../navigation-drawer.service';

@Component({
    selector: 'web-navigation-tab-item',
    templateUrl: './navigation-tab-item.component.html',
    styleUrls: ['./navigation-tab-item.component.less'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [BsDropdownModule, TranslateModule, NavigationGridItemComponent],
})
export class NavigationTabItemComponent {
    public readonly entity = input.required<NormalizedTabEntity>();
    public readonly index = input.required<number>();
    public readonly selectedEntity = input.required<SelectedEntity>();

    private readonly layoutFacade: LayoutFacade = inject(LayoutFacade);
    private readonly tabsFacade: TabsFacade = inject(TabsFacade);
    private readonly gridsFacade: GridsFacade = inject(GridsFacade);
    private readonly tabFormService: WebTabFormService = inject(WebTabFormService);
    private readonly navDrawerService: NavigationDrawerService = inject(NavigationDrawerService);

    public readonly showChildren = computed(() => this.navDrawerService.hasTabId(this.entity().id));

    public readonly isActive = computed(() => this.selectedEntity()?.entityId === this.entity().id);

    public readonly displayId = computed(() => 'tab_' + this.entity().id);

    private readonly gridsEntities = toSignal(this.gridsFacade.gridsEntities$, { initialValue: {} });

    /** The grids backing each tab, in tab order. */
    public readonly grids = computed(() => {
        const grids = this.gridsEntities();
        return (this.entity().children as string[]).map((id) => grids[id] as NormalizedGridEntity);
    });

    public selectEntity(): void {
        if (this.navDrawerService.hasTabId(this.entity().id)) {
            this.navDrawerService.removeTabId(this.entity().id);
        } else {
            this.navDrawerService.addTabId(this.entity().id);
        }

        if (this.isActive()) {
            return;
        }

        this.layoutFacade.setSelectedEntityWithScroll({
            entityId: this.entity().id,
            entityType: ESelectedEntityType.tab,
        });
    }

    public async removeTab(): Promise<void> {
        const tab = await firstValueFrom(this.tabsFacade.denormalizedById$(this.entity().id));

        if (this.isActive()) {
            this.layoutFacade.unsetEntity();
        }

        this.tabsFacade.remove(tab, this.index());
    }

    public async copyTab(): Promise<void> {
        const tab = await firstValueFrom(this.tabsFacade.denormalizedById$(this.entity().id));

        this.tabsFacade.copy(tab, this.index());
    }

    public onChildGridSelected(gridId: string): void {
        const tabs = this.entity().props?.tabs ?? [];
        const targetIndex = tabs.findIndex((tab) => tab.contentEntityId === gridId);

        if (targetIndex === -1 || tabs[targetIndex].isActive) {
            return;
        }

        // Prefer the live editor form (records history + plays the switch animation);
        // fall back to a direct store update when the tab is not currently rendered.
        const form = this.tabFormService.getForm(this.entity().id);

        if (form) {
            form.updateTabs((current) => current.map((tab, index) => ({ ...tab, isActive: index === targetIndex })));
            return;
        }

        this.tabsFacade.updateProperties(this.entity().id, {
            props: {
                ...this.entity().props,
                tabs: tabs.map((tab, index) => ({ ...tab, isActive: index === targetIndex })),
            },
        });
    }
}
